import { __rest } from "tslib";
import * as React from 'react';
import { createPortal } from 'react-dom';
import { css } from '@patternfly/react-styles';
import { shallowLeftSideEquals, getBoundingClientRect, getClientWindowDimensions } from '../../utils/utils';
import { inlineEditStyles as styles } from './css/inline-edit-css';
import { ConfirmButtons } from '../ConfirmButtons';
import '@patternfly/react-styles/css/components/Table/inline-edit.css';
// TableEditConfirmation constants like TABLE_TOP cannot be referenced but must be hardcoded due to this issue:
// https://github.com/reactjs/react-docgen/issues/317#issue-393678795
const tableConfirmationMapper = {
    TABLE_TOP: {
        hasConfirmationButtons: ({ isTableEditing, isFirstVisible }) => isTableEditing && isFirstVisible,
        isTableConfirmation: () => true,
        areButtonsOnTop: () => true,
        hasBoldBorder: () => true,
        getEditStyles: ({ isTableEditing, isFirstVisible }) => css(styles.tableEditableRow, isTableEditing && isFirstVisible && styles.modifiers.tableEditingFirstRow)
    },
    TABLE_BOTTOM: {
        hasConfirmationButtons: ({ isTableEditing, isLastVisible }) => isTableEditing && isLastVisible,
        isTableConfirmation: () => true,
        areButtonsOnTop: () => false,
        hasBoldBorder: () => true,
        getEditStyles: ({ isTableEditing, isLastVisible }) => css(styles.tableEditableRow, isTableEditing && isLastVisible && styles.modifiers.tableEditingLastRow)
    },
    ROW: {
        hasConfirmationButtons: ({ isEditing, isParentEditing, isLastVisibleParent, isChildEditing, isLastVisible }) => isEditing &&
            !(isChildEditing && isParentEditing) && // buttons can't appear in the middle
            !(isParentEditing && isLastVisible) && // parent will show the buttons on top
            !(isChildEditing && !isLastVisibleParent),
        isTableConfirmation: () => false,
        areButtonsOnTop: ({ isLastVisible, isLastVisibleParent }) => isLastVisible || isLastVisibleParent,
        hasBoldBorder: () => false,
        getEditStyles: ({ isEditing }) => css(styles.tableEditableRow, isEditing && styles.modifiers.editing)
    },
    NO_CONFIRM_ROW: {
        hasConfirmationButtons: () => false,
        isTableConfirmation: () => false,
        areButtonsOnTop: () => false,
        hasBoldBorder: () => false,
        getEditStyles: ({ isEditing }) => css(styles.tableEditableRow, isEditing && styles.modifiers.editing)
    },
    NONE: {
        hasConfirmationButtons: () => false,
        isTableConfirmation: () => false,
        areButtonsOnTop: () => false,
        hasBoldBorder: () => false,
        getEditStyles: () => css(styles.tableEditableRow)
    }
};
const getTableConfirmation = ({ editConfig }) => (editConfig && tableConfirmationMapper[editConfig.editConfirmationType]) ||
    tableConfirmationMapper.NONE;
export const editableRowWrapper = (RowWrapperComponent) => { var _a; return _a = class EditableRowWrapper extends React.Component {
        constructor() {
            super(...arguments);
            this.state = Object.assign({ hasConfirmationButtons: false, window: undefined, rowDimensions: undefined }, EditableRowWrapper.getDerivedStateFromProps(this.props));
            this.element = React.createRef();
            this.setStateWith2dEquals = (newState) => {
                this.setState(oldState => Object.keys(newState).find(key => !shallowLeftSideEquals(newState[key], oldState[key]))
                    ? newState
                    : null);
            };
            this.updateRowDimensions = () => {
                if (this.element.current) {
                    this.setStateWith2dEquals({
                        rowDimensions: getBoundingClientRect(this.element.current)
                    });
                }
            };
            this.handleScroll = () => {
                this.updateRowDimensions();
            };
            this.handleResize = () => {
                this.fetchClientDimensions();
                this.updateRowDimensions();
            };
        }
        componentDidMount() {
            this.tableElem = this.element.current.closest('table');
            if (typeof this.props.trRef === 'function') {
                this.props.trRef(this.element.current);
            }
            this.updateRowDimensions();
            if (this.state.hasConfirmationButtons) {
                this.fetchClientDimensions();
            }
        }
        fetchClientDimensions() {
            this.setStateWith2dEquals({
                window: getClientWindowDimensions()
            });
        }
        getConfirmationButtons() {
            const _a = this.props, { row, rowProps } = _a, props = __rest(_a, ["row", "rowProps"]);
            const { isLastVisible, isParentEditing, isLastVisibleParent, editConfig } = row;
            if (!editConfig) {
                return null;
            }
            const { onEditConfirmed, onEditCanceled } = editConfig;
            const tableConfirmation = getTableConfirmation(row);
            let confirmButtons;
            if (this.element.current && this.state.rowDimensions) {
                const options = tableConfirmation.isTableConfirmation() ? {} : rowProps;
                const actionObject = tableConfirmation.isTableConfirmation() ? null : row;
                confirmButtons = createPortal(React.createElement(ConfirmButtons, Object.assign({}, props, { onConfirm: event => onEditConfirmed(event, actionObject, options), onCancel: event => onEditCanceled(event, actionObject, options), buttonsOnTop: tableConfirmation.areButtonsOnTop({ isLastVisible, isParentEditing, isLastVisibleParent }), boldBorder: tableConfirmation.hasBoldBorder(), environment: {
                        window: this.state.window,
                        row: getBoundingClientRect(this.element.current)
                    } })), this.tableElem ? this.tableElem.parentNode : document.body);
            }
            return confirmButtons;
        }
        render() {
            const { className, onScroll, onResize, row: { isFirstVisible, isLastVisible, isEditing, isTableEditing, editConfig } } = this.props;
            const { hasConfirmationButtons } = this.state;
            const trClassName = getTableConfirmation({ editConfig }).getEditStyles({
                isEditing,
                isTableEditing,
                isFirstVisible,
                isLastVisible
            });
            return (React.createElement(React.Fragment, null,
                React.createElement(RowWrapperComponent, Object.assign({}, this.props, { trRef: this.element, className: css(trClassName, className), onScroll: event => {
                        if (hasConfirmationButtons) {
                            this.handleScroll();
                        }
                        onScroll(event);
                    }, onResize: event => {
                        if (hasConfirmationButtons) {
                            this.handleResize();
                        }
                        onResize(event);
                    } })),
                hasConfirmationButtons && this.getConfirmationButtons()));
        }
    },
    _a.defaultProps = {
        onScroll: () => { },
        onResize: () => { }
    },
    _a.getDerivedStateFromProps = (props) => ({
        hasConfirmationButtons: getTableConfirmation(props.row).hasConfirmationButtons(props.row)
    }),
    _a; };
//# sourceMappingURL=editableRowWrapper.js.map